<script lang="ts" setup>
import DropDown, { type DropDownItem } from '@/components/common/DropDown.vue';
import { fortnoxUsersService } from '@/services/fortnox-users/fortnox-users-service';
import { useQuery } from '@tanstack/vue-query';
import { UserRole, UserStatus } from 'cesg-shared/dist/models';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { CESGPickerActionEvent } from './types';

interface Props {
  value?: string;
  excludeIds?: string[];
}

const props = defineProps<Props>();
const emit = defineEmits<{ action: [event: CESGPickerActionEvent] }>();

const { data, isPending, isError } = useQuery({
  queryKey: ['fortnox-users', 'getAll'],
  queryFn: () => fortnoxUsersService.getAll(),
});

const cesgUsers = computed(() =>
  (data.value ?? []).filter(
    (user) =>
      (user.role === UserRole.CESG && user.status === UserStatus.ACTIVE) ||
      user.role === UserRole.ADMIN,
  ),
);

const dropdownItems = computed<DropDownItem[]>(() =>
  cesgUsers.value
    .filter((user) => !props.excludeIds?.includes(user.id))
    .map((user) => ({
      value: user.id,
      text: `${user.name} — ${user.email}`,
    })),
);

const isDisabled = computed(() => isPending.value || isError.value);

const { t } = useI18n();

const selectedId = ref(props.value ?? '');

watch(
  () => props.value,
  (v) => {
    selectedId.value = v ?? '';
  },
);

const onSelect = (id: string) => {
  const user = cesgUsers.value.find((u) => u.id === id);
  if (user) {
    emit('action', {
      type: 'select',
      data: { id: user.id, name: user.name, email: user.email },
    });
  }
  if (!props.value) {
    selectedId.value = '';
  }
};
</script>

<template>
  <div class="cesg-picker">
    <DropDown
      :items="dropdownItems"
      :modelValue="selectedId"
      :label="t('overview.projectOrganisations.pickCesgUser')"
      :disabled="isDisabled"
      :teleport="true"
      @update:modelValue="onSelect"
    />
  </div>
</template>
